import { Button } from '@ads/components-react';
import {
  ArrowUpTrayIcon,
  ClockIcon,
  DocumentTextIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

/** Ações da régua da tela de edição de parcela. */
export type ParcelEditToolbarAction =
  | 'orderTracking'
  | 'traceability'
  | 'billetHistory'
  | 'invoiceUpload';

/** Props de `ParcelEditToolbar`. */
export interface ParcelEditToolbarProps {
  readonly onAction: (action: ParcelEditToolbarAction) => void;
  /** Data da última integração da parcela, já formatada; ausente, nada é exibido. */
  readonly lastIntegrationLabel?: string | null | undefined;
}

const ACTIONS: readonly {
  readonly action: ParcelEditToolbarAction;
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
}[] = [
  { action: 'orderTracking', icon: TruckIcon },
  { action: 'traceability', icon: ClockIcon },
  { action: 'billetHistory', icon: DocumentTextIcon },
  { action: 'invoiceUpload', icon: ArrowUpTrayIcon },
];

/**
 * Régua de ações da parcela, no topo da tela de edição.
 *
 * São as quatro ações que a tela legada põe como botões destacados. Duas delas —
 * os rastreamentos — abrem o `ParcelHistoryDrawer` que a tela de parcelas já usa,
 * com a mesma regra de uma requisição por abertura. As outras duas seguem no
 * padrão de "não implementado" da feature.
 *
 * Todas são **secundárias** aqui, ao contrário do legado, onde as quatro
 * competiam visualmente com o Salvar: são atalhos de consulta, não a ação
 * principal da tela.
 *
 * Segue o frame `Upir5` de `design/payment.pen`.
 */
export function ParcelEditToolbar({ onAction, lastIntegrationLabel }: ParcelEditToolbarProps) {
  const { t } = useTranslation('individualPayment');

  // Chaves literais para que `t()` valide em tempo de compilação.
  const label = {
    orderTracking: t('parcelEdit.toolbar.orderTracking'),
    traceability: t('parcelEdit.toolbar.traceability'),
    billetHistory: t('parcelEdit.toolbar.billetHistory'),
    invoiceUpload: t('parcelEdit.toolbar.invoiceUpload'),
  } as const satisfies Record<ParcelEditToolbarAction, string>;

  return (
    <div className="flex flex-wrap items-center gap-micro">
      {ACTIONS.map(({ action, icon }) => (
        <Button
          key={action}
          hierarchy="secondary"
          size="sm"
          leadingIcon={icon}
          onClick={() => onAction(action)}
        >
          {label[action]}
        </Button>
      ))}

      {lastIntegrationLabel ? (
        <span className="ml-auto ts-paragraph-xxs text-ctx-content-placeholder">
          {t('parcelEdit.toolbar.lastIntegration', { date: lastIntegrationLabel })}
        </span>
      ) : null}
    </div>
  );
}
